import React from 'react';
import { LegalPageLayout } from './LegalPageLayout';

const Section = ({
  number,
  title,
  children,
}: {
  number?: string;
  title: string;
  children: React.ReactNode;
}) => (
  <section className="p-6 md:p-8 rounded-3xl bg-zinc-900/40 border border-white/5 backdrop-blur-sm">
    {number && (
      <div className="flex items-center gap-3 mb-4">
        <span className="w-8 h-8 rounded-lg bg-blue-500/20 border border-blue-500/30 flex items-center justify-center text-blue-400 font-bold text-sm">
          {number}
        </span>
        <h2 className="text-white font-bold text-xl md:text-2xl">{title}</h2>
      </div>
    )}
    {!number && <h2 className="text-white font-bold text-xl md:text-2xl mb-4">{title}</h2>}
    <div className="text-zinc-400 leading-relaxed text-base md:text-lg space-y-4">{children}</div>
  </section>
);

const HighlightBox = ({
  children,
  color = 'blue',
}: {
  children: React.ReactNode;
  color?: 'blue' | 'purple' | 'emerald' | 'amber';
}) => {
  const colors = {
    blue: 'from-blue-500/10 to-blue-500/5 border-blue-500/20 text-blue-400',
    purple: 'from-purple-500/10 to-purple-500/5 border-purple-500/20 text-purple-400',
    emerald: 'from-emerald-500/10 to-emerald-500/5 border-emerald-500/20 text-emerald-400',
    amber: 'from-amber-500/10 to-amber-500/5 border-amber-500/20 text-amber-400',
  };
  return (
    <div className={`p-5 rounded-2xl bg-gradient-to-br ${colors[color]} border backdrop-blur-sm`}>
      {children}
    </div>
  );
};

export const TermsOfServicePage: React.FC = () => {
  return (
    <LegalPageLayout title="Terms of Service" lastUpdated="February 27, 2026" icon="file">
      <section className="p-6 md:p-8 rounded-3xl bg-gradient-to-br from-zinc-900/60 to-zinc-800/40 border border-white/8 backdrop-blur-sm mb-8">
        <h2 className="text-white font-bold text-2xl md:text-3xl mb-4">Welcome to Musicade</h2>
        <p className="text-zinc-400 leading-relaxed text-base md:text-lg">
          These Terms of Service ("Terms") govern your access to and use of the Musicade platform
          operated by Musicade LLC, doing business as "Musicade" ("Musicade," "we," "our," or "us").
          These Terms form a legally binding agreement between you ("User," "Creator," or "you") and
          Musicade.
        </p>
        <p className="text-zinc-400 leading-relaxed text-base md:text-lg mt-4">
          By creating an account, accessing, or using the Platform, you acknowledge that you have
          read, understood, and agree to be bound by these Terms and all applicable laws and
          regulations. If you do not agree to these Terms, you must not access or use the Platform.
        </p>
      </section>

      <Section number="1" title="Introduction & Acceptance of Terms">
        <p>
          Musicade provides a digital platform that enables users to create, customize, manage,
          promote, and publish events, as well as sell tickets through integrated payment processing
          services. These Terms apply to all users of the Platform, including Creators,
          Collaborators (editors), and attendees interacting with events.
        </p>
      </Section>

      <Section number="2" title="Definitions">
        <ul className="space-y-3 list-none">
          <li>
            <strong className="text-white">"Platform"</strong> — refers to the Musicade website,
            mobile applications, and all related features and services.
          </li>
          <li>
            <strong className="text-white">"User"</strong> — means any individual or entity that
            accesses or uses the Platform.
          </li>
          <li>
            <strong className="text-white">"Creator"</strong> — means a User who creates, publishes,
            or manages events on the Platform.
          </li>
          <li>
            <strong className="text-white">"Collaborator"</strong> — means a User granted limited
            editor permissions by a Creator to assist with managing an event, without ownership
            rights.
          </li>
          <li>
            <strong className="text-white">"Event"</strong> — means any listing, experience,
            activity, or gathering created and published through the Platform.
          </li>
        </ul>
      </Section>

      <Section number="3" title="Eligibility & Accounts">
        <p>
          You must be at least 16 years old to create an account or use Musicade. If you are between
          13 and 16 years old, you may only use the Platform with the consent and supervision of a
          parent or legal guardian. By using the Platform, you represent and warrant that:
        </p>
        <ul className="space-y-2 list-disc pl-6 mt-4">
          <li>You meet the minimum age requirement;</li>
          <li>The information you provide is accurate, current, and complete;</li>
          <li>You will maintain the confidentiality of your login credentials; and</li>
          <li>You are responsible for all activities that occur under your account.</li>
        </ul>
      </Section>

      <Section number="4" title="Platform Description">
        <HighlightBox color="blue">
          <p className="text-blue-300">
            <strong className="text-white">Important:</strong> Musicade acts solely as a facilitator
            and technology provider. Unless explicitly stated otherwise, Musicade is not the
            organizer, host, promoter, or manager of any event listed on the Platform.
          </p>
        </HighlightBox>
      </Section>

      <Section number="5" title="Creator Responsibilities">
        <p>Creators are solely and fully responsible for:</p>
        <ul className="space-y-2 list-disc pl-6 mt-4">
          <li>The accuracy and completeness of event descriptions;</li>
          <li>Event logistics, planning, safety measures, and execution;</li>
          <li>Compliance with all applicable laws and permits;</li>
          <li>Managing ticketing obligations and refunds.</li>
        </ul>
      </Section>

      <Section number="6" title="Content Ownership & License">
        <p>
          Users retain ownership of the Content they upload. By posting Content, you grant Musicade
          a non-exclusive, royalty-free license to host, display, and distribute such Content solely
          for the purpose of operating and providing the Platform services.
        </p>
      </Section>

      <Section number="7" title="Collaborator Permissions">
        <p>
          Creators may assign Collaborators with limited editing permissions. Collaborators do not
          obtain ownership rights. Creators remain responsible for all actions taken by their
          Collaborators.
        </p>
      </Section>

      <Section number="8" title="Platform Rights">
        <p>Musicade reserves the right to:</p>
        <ul className="space-y-2 list-disc pl-6 mt-4">
          <li>Review, modify, or remove any Content or Event;</li>
          <li>Suspend or terminate accounts engaged in prohibited conduct;</li>
          <li>Investigate violations and take appropriate enforcement actions.</li>
        </ul>
      </Section>

      <Section number="9" title="General Disclaimers">
        <HighlightBox color="amber">
          <p className="text-amber-300">
            <strong className="text-white">Disclaimer:</strong> Musicade does not guarantee event
            success, attendance levels, or financial outcomes. Users attend and participate in
            events at their own risk. Musicade is not responsible for real-world incidents occurring
            at events.
          </p>
        </HighlightBox>
      </Section>

      <Section number="10" title="Intellectual Property">
        <p>
          All intellectual property rights related to the Platform are owned by Musicade or its
          licensors. Unauthorized copying or modification is strictly prohibited.
        </p>
      </Section>

      <Section number="11" title="Limitation of Liability">
        <p>
          To the fullest extent permitted by law, Musicade shall not be liable for any indirect,
          incidental, or consequential damages, including personal injuries or property damage
          occurring at events organized by Creators.
        </p>
      </Section>

      <Section number="12" title="Indemnification">
        <p>
          You agree to indemnify and hold harmless Musicade from and against any third-party claims
          arising from your Events, Content, or violation of these Terms.
        </p>
      </Section>

      <Section number="13" title="Termination & Suspension">
        <p>
          Musicade may suspend or terminate your access for cause, including security risks, illegal
          activity, or severe/repeated violations of these Terms.
        </p>
      </Section>

      <Section number="14" title="Changes to Terms">
        <p>
          Musicade reserves the right to update or modify these Terms at any time. Continued use of
          the Platform after updates constitutes acceptance of the modified Terms.
        </p>
      </Section>

      <Section number="15" title="Dispute Resolution">
        <p>
          These Terms shall be governed by the laws of the State of New York. Disputes shall be
          resolved through binding arbitration administered by the American Arbitration Association
          (AAA).
        </p>
      </Section>

      <section className="p-6 md:p-8 rounded-3xl bg-zinc-900/40 border border-white/5 backdrop-blur-sm">
        <h2 className="text-white font-bold text-xl md:text-2xl mb-4">Contact Information</h2>
        <p className="text-zinc-400 leading-relaxed text-base md:text-lg mb-4">
          For questions or legal inquiries regarding these Terms, please contact:
        </p>
        <div className="p-4 rounded-2xl bg-zinc-800/40 border border-white/5">
          <p className="text-white font-semibold">Legal Department</p>
          <p className="text-zinc-400">Musicade LLC (DBA Musicade)</p>
          <a
            href="mailto:support@musicade.com"
            className="text-blue-400 hover:text-blue-300 transition-colors"
          >
            support@musicade.com
          </a>
        </div>
      </section>
    </LegalPageLayout>
  );
};
